/**
 * Driver GPS Hook
 * Manages GPS tracking and broadcasting for drivers
 * Sends location to both driver service AND live tracking for active deliveries
 */
import { useState, useEffect, useCallback, useRef } from "react";
import api from "../lib/api";

const DEFAULT_OPTIONS = {
  enableHighAccuracy: true,
  timeout: 10000,
  maximumAge: 5000,
};

export const useDriverGPS = (activeTrackingCodes = [], updateInterval = 10000) => {
  const [location, setLocation] = useState(null);
  const [error, setError] = useState(null);
  const [isTracking, setIsTracking] = useState(false);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [batteryLevel, setBatteryLevel] = useState(null);
  
  const watchIdRef = useRef(null);
  const intervalRef = useRef(null);
  const trackingCodesRef = useRef(activeTrackingCodes);
  
  // Update tracking codes ref when prop changes
  useEffect(() => {
    trackingCodesRef.current = activeTrackingCodes;
  }, [activeTrackingCodes]);
  
  // Get battery level if available
  useEffect(() => {
    if ("getBattery" in navigator) {
      navigator.getBattery().then(battery => {
        setBatteryLevel(Math.round(battery.level * 100));
        battery.addEventListener("levelchange", () => {
          setBatteryLevel(Math.round(battery.level * 100));
        });
      });
    }
  }, []);
  
  // Send location to server
  const sendLocationUpdate = useCallback(async (position) => {
    const locationData = {
      latitude: position.coords.latitude,
      longitude: position.coords.longitude,
      accuracy: position.coords.accuracy,
      speed: position.coords.speed ? position.coords.speed * 3.6 : 0, // m/s to km/h
      heading: position.coords.heading || 0,
      altitude: position.coords.altitude,
      battery_level: batteryLevel,
    };
    
    setLocation(locationData);
    setLastUpdate(new Date());
    
    // Send to general driver GPS endpoint
    try {
      await api.post("/drivers/gps/update", locationData);
    } catch (err) {
      console.error("Driver GPS update error:", err);
    }
    
    // Send to live tracking for each active delivery
    const codes = trackingCodesRef.current;
    if (codes && codes.length > 0) {
      for (const trackingCode of codes) {
        try {
          await api.post(`/live-tracking/driver/update-location?tracking_code=${trackingCode}`, locationData);
          console.log(`Live tracking updated for ${trackingCode}`);
        } catch (err) {
          console.error(`Live tracking update error for ${trackingCode}:`, err);
        }
      }
    }
  }, [batteryLevel]);
  
  // Handle GPS error
  const handleError = useCallback((err) => {
    let message = "Unable to get location";
    switch (err.code) {
      case err.PERMISSION_DENIED:
        message = "Location permission denied. Please enable GPS.";
        break;
      case err.POSITION_UNAVAILABLE:
        message = "Location unavailable. Check GPS settings.";
        break;
      case err.TIMEOUT:
        message = "Location request timed out.";
        break;
      default:
        message = err.message || "Unknown GPS error";
    }
    setError(message);
  }, []);
  
  // Start tracking
  const startTracking = useCallback(() => {
    if (!navigator.geolocation) {
      setError("Geolocation not supported");
      return;
    }
    
    setError(null);
    setIsTracking(true);
    
    // Initial position
    navigator.geolocation.getCurrentPosition(
      sendLocationUpdate,
      handleError,
      DEFAULT_OPTIONS
    );
    
    // Watch position continuously
    watchIdRef.current = navigator.geolocation.watchPosition(
      sendLocationUpdate,
      handleError,
      DEFAULT_OPTIONS
    );
    
    // Also send updates at regular intervals as backup
    intervalRef.current = setInterval(() => {
      navigator.geolocation.getCurrentPosition(
        sendLocationUpdate,
        handleError,
        { ...DEFAULT_OPTIONS, maximumAge: 0 }
      );
    }, updateInterval);
    
  }, [sendLocationUpdate, handleError, updateInterval]);
  
  // Stop tracking
  const stopTracking = useCallback(() => {
    if (watchIdRef.current !== null) {
      navigator.geolocation.clearWatch(watchIdRef.current);
      watchIdRef.current = null;
    }
    
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    
    setIsTracking(false);
  }, []);
  
  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stopTracking();
    };
  }, [stopTracking]);
  
  return {
    location,
    error,
    isTracking,
    lastUpdate,
    batteryLevel,
    startTracking,
    stopTracking,
  };
};

export default useDriverGPS;
